import type { Locale } from '../contexts/LocaleContext'
import { company, derived } from './company'

import privacyFr from './privacy.fr.md?raw'
import privacyEn from './privacy.en.md?raw'
import termsFr from './terms.fr.md?raw'
import termsEn from './terms.en.md?raw'
import noticeFr from './notice.fr.md?raw'
import noticeEn from './notice.en.md?raw'
import deletionFr from './data-deletion.fr.md?raw'
import deletionEn from './data-deletion.en.md?raw'

// The legal section (spec 2026-09-09-02), published at `/about`.
//
// The module, its symbols and its test ids keep the name `legal` while the
// public prefix is `/about`: what these files hold IS the legal corpus
// (privacy policy, terms, LCEN notice, deletion procedure), and `/about` is
// only where a visitor is invited to find it. Grep `legal` for the documents,
// `about` for the routes.
//
// Legal text is prose, not UI copy: it lives in Markdown files, one per
// document and language, and NOT in the i18n JSON bundles. Two reasons — a
// privacy policy is read and amended as a whole document by whoever is
// responsible for it, and splitting it into interpolated keys would make the
// sentence a translator sees meaningless.
//
// Every company fact is substituted from ./company at render time, so a
// change of address or registration number cannot leave one of the eight
// files behind.

/** Slugs are English, like every route in this codebase; the copy is French first. */
export const LEGAL_SLUGS = ['privacy', 'terms', 'notice', 'data-deletion'] as const

export type LegalSlug = (typeof LEGAL_SLUGS)[number]

export function isLegalSlug(value: string): value is LegalSlug {
    return (LEGAL_SLUGS as readonly string[]).includes(value)
}

const SOURCES: Record<LegalSlug, Record<Locale, string>> = {
    privacy: { fr: privacyFr, en: privacyEn },
    terms: { fr: termsFr, en: termsEn },
    notice: { fr: noticeFr, en: noticeEn },
    'data-deletion': { fr: deletionFr, en: deletionEn },
}

/**
 * substitute replaces every `{{company.x}}` / `{{doc.updated}}` placeholder.
 *
 * An unknown placeholder is removed rather than printed: a raw `{{company.vat}}`
 * on a published legal notice reads as a bug to the one reader who matters, and
 * an empty optional fact is a deliberate omission (see company.ts).
 */
function substitute(markdown: string, locale: Locale): string {
    const values: Record<string, string> = {
        ...Object.fromEntries(Object.entries(company).map(([k, v]) => [`company.${k}`, String(v)])),
        ...Object.fromEntries(Object.entries(derived).map(([k, v]) => [`company.${k}`, String(v)])),
        'doc.updated': formatUpdated(company.updated, locale),
    }

    return markdown
        .replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key: string) => values[key] ?? '')
        // A removed optional line leaves a blank line behind; collapse the run
        // so the rendered paragraph does not split in two.
        .replace(/\n{3,}/g, '\n\n')
}

function formatUpdated(iso: string, locale: Locale): string {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso

    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(date)
}

/** The rendered Markdown of one document, company facts already substituted. */
export function legalDocument(slug: LegalSlug, locale: Locale): string {
    return substitute(SOURCES[slug][locale] ?? SOURCES[slug].fr, locale)
}

/**
 * The "last updated" date of the section, already formatted for the locale.
 * The index prints it once for the whole section; each document repeats it in
 * its own last line, from the same `company.updated`.
 */
export function legalUpdated(locale: Locale): string {
    return formatUpdated(company.updated, locale)
}
